import { Fragment, useEffect, useRef, useState } from 'react';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import { useTranslation } from 'react-i18next';

import { Check } from '@unified-checkout/design-system/icons/index';
import { Button, SkeletonLoader } from '@unified-checkout/design-system';

import {
  getApiKey,
  getHmacKey,
  getPublicSecretKey,
} from '@app/dashboard/services/inner_layout/api_keys';
import { notify } from '@app/dashboard/utils/notifier';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import {
  API_KEYS_EVENTS,
  API_KEYS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/api-keys';
import {
  ACTIONS,
  API_KEYS_CARDS,
} from '@app/dashboard/constants/settings/api-keys';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES,
} from '@app/dashboard/constants/tracking_events/system';
import type {
  ICurrentCardTypeAction,
  TApiKeysActions,
  TApiKeysCardType,
} from '@app/dashboard/types/innerLayout/settings/api_keys';

import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import {
  getKeysPermissions,
  shouldHideCard,
  checkIfStaff,
  shouldHideBtns,
  getErrorMessage,
  handleCopyText,
  checkIfServerError,
} from '../utils';

import ViewRecreateModal from './ViewRecreateModal';

import styles from './ApiKeysCards.module.scss';

const ApiKeysCards = () => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const isFetching = useIsFetching();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isCopied, setIsCopied] = useState(false);
  const [currentCardTypeAction, setCurrentCardTypeAction] =
    useState<ICurrentCardTypeAction>({
      type: null,
      action: null,
    });

  const [isViewRecreateModalOpen, setIsViewRecreateModalOpen] = useState(false);

  const permissions = getKeysPermissions();

  const {
    data: apiKey,
    isError: isApiKeyError,
    error: apiKeyError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_API_KEY],
    queryFn: getApiKey,
    select: (response) => response.data?.api_key,
    enabled: permissions.canViewApiKey,
  });

  const {
    data: publicSecretKey,
    isError: isPublicSecretKeyError,
    error: publicSecretKeyError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_PUBLIC_SECRET_KEY],
    queryFn: getPublicSecretKey,
    select: (response) => response.data,
    enabled: permissions.canViewPublicSecretKey,
  });

  const {
    data: hmac,
    isError: isHmacError,
    error: hmacError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_HMAC],
    queryFn: getHmacKey,
    select: (response) => response.data?.hmac_secret,
    enabled: !checkIfStaff(),
  });

  const handleCopy = (type: TApiKeysCardType) => {
    const keyMap = {
      apiKey: apiKey,
      publicKey:
        publicSecretKey?.[isLive ? 'live_public_key' : 'test_public_key'] || '',
      secretKey:
        publicSecretKey?.[isLive ? 'live_secret_key' : 'test_secret_key'] || '',
      hmac: hmac || '',
    };

    const textToCopy = keyMap[type];
    handleCopyText({ textToCopy, copyTimeoutRef, setIsCopied });
  };

  const isCopiedBtn = (type: TApiKeysCardType, key: TApiKeysActions) => {
    return (
      isCopied && key === ACTIONS.COPY && currentCardTypeAction.type === type
    );
  };

  const handleOpenModal = () => {
    setIsViewRecreateModalOpen(true);
  };

  const handleBtnClick = ({ action, type }: ICurrentCardTypeAction) => {
    if (!action || !type) return;
    setCurrentCardTypeAction({ type, action });

    const actionHandlers = {
      copy: handleCopy,
      view: handleOpenModal,
      recreate: handleOpenModal,
    };
    const handler = actionHandlers[action];
    if (handler) {
      handler(type);
    }
  };

  useEffect(() => {
    amplitude.track(API_KEYS_EVENTS.VIEW_API_KEYS_PAGE, {
      [API_KEYS_EVENTS_PARAMS.SUBSECTION]: API_KEYS_EVENTS_PARAMS.API_KEYS,
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
    });
  }, []);

  const errorMessage =
    getErrorMessage(apiKeyError) ||
    getErrorMessage(publicSecretKeyError) ||
    getErrorMessage(hmacError);

  const trackingCode = apiKeyError
    ? SYSTEM_EVENTS_GROUPING_CODES.API_KEYS.GET.API
    : publicSecretKeyError
    ? SYSTEM_EVENTS_GROUPING_CODES.API_KEYS.GET.PUBLIC_SECRET
    : SYSTEM_EVENTS_GROUPING_CODES.API_KEYS.GET.HMAC;

  useEffect(() => {
    if (errorMessage) {
      notify.error(errorMessage, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode,
            msg: errorMessage as string,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.API_KEYS,
          }),
      });
    }
  }, [errorMessage]);

  return (
    <div className={styles['api-keys-cards']}>
      {currentCardTypeAction.action !== ACTIONS.COPY && (
        <ViewRecreateModal
          isViewRecreateModalOpen={isViewRecreateModalOpen}
          setIsViewRecreateModalOpen={setIsViewRecreateModalOpen}
          currentCardTypeAction={currentCardTypeAction}
          apiKeysData={{
            apiKey,
            publicKey:
              publicSecretKey?.[isLive ? 'live_public_key' : 'test_public_key'],
            secretKey:
              publicSecretKey?.[isLive ? 'live_secret_key' : 'test_secret_key'],
            hmac,
          }}
        />
      )}
      

      {API_KEYS_CARDS(t).map(
        ({ title, description, actions, hasTestLiveTag, type }, index) => {
          const hiddenCard = shouldHideCard(type);
          if (hiddenCard) return null;

          const hasServerError = checkIfServerError({
            type,
            isApiKeyError,
            isPublicSecretKeyError,
            isHmacError,
          });

          return !!isFetching ? (
            <div className={styles['api-keys-cards__card--loader']} key={index}>
              <SkeletonLoader
                noOfRows={4}
                type="bordered_box"
                height="10rem"
                width="100%"
              />
            </div>
          ) : (
            <div className={styles['api-keys-cards__card']} key={title}>
              <div className={styles['api-keys-cards__card-header']}>
                <span className={styles['api-keys-cards__card-header-title']}>
                  {title}
                </span>
                {hasTestLiveTag && (
                  <span
                    className={styles['api-keys-cards__card-header-status']}
                    data-api-key-status={isLive ? 'live' : 'test'}
                  >
                    {t(
                      `innerLayout.apiKeys.status.${isLive ? 'live' : 'test'}`
                    )}
                  </span>
                )}
              </div>
              <p className={styles['api-keys-cards__card-desc']}>
                {description}
              </p>
              <div className={styles['api-keys-cards__card-actions']}>
                {actions.map(({ key, label }) => {
                  const isBtnHidden = shouldHideBtns({
                    type,
                    action: key as TApiKeysActions,
                    permissions,
                  });

                  if (isBtnHidden) return null;

                  const isCopied = isCopiedBtn(type, key as TApiKeysActions);

                  return (
                    <Fragment key={key}>
                      <Button
                        variant="outlined"
                        size="sm"
                        text={
                          isCopied
                            ? t('innerLayout.apiKeys.actions.copied')
                            : label
                        }
                        leadingIcon={isCopied ? Check : undefined}
                        color="secondary"
                        onClickHandler={() =>
                          handleBtnClick({
                            action: key as TApiKeysActions,
                            type,
                          })
                        }
                        disabled={isCopied || hasServerError}
                      />
                    </Fragment>
                  );
                })}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default ApiKeysCards;
